/**
 * Certificates API
 * Handles certificate and identity verification API calls
 */

import { api } from "@/lib/api";

// Types
export interface Certificate {
  id: string;
  userId: string;
  courseId: string;
  code: string;
  recipientName: string;
  courseTitle: string;
  hoursCompleted: number;
  finalExamScore: number | null;
  identityVerified: boolean;
  verificationId: string | null;
  pdfUrl: string | null;
  issuedAt: string;
  revokedAt: string | null;
  course?: {
    id: string;
    title: string;
    slug: string;
    thumbnailUrl: string | null;
  };
}

export interface CertificateEligibility {
  eligible: boolean;
  reasons: string[];
  missingRequirements: string[];
}

export interface CertificateVerification {
  valid: boolean;
  message?: string;
  certificate?: {
    code: string;
    recipientName: string;
    courseTitle: string;
    hoursCompleted: number;
    finalExamScore: number | null;
    identityVerified: boolean;
    issuedAt: string;
    course: {
      id: string;
      title: string;
      slug: string;
      thumbnailUrl: string | null;
    };
  };
  revokedAt?: string;
  revocationReason?: string;
}

export type VerificationStatus =
  | "NOT_SUBMITTED"
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "EXPIRED";

export interface VerificationStatusResponse {
  status: VerificationStatus;
  message: string;
  verifiedName?: string;
  verifiedAt?: string;
  expiresAt?: string;
  reason?: string;
  submittedAt?: string;
  rejectedAt?: string;
  expiredAt?: string;
}

export type DocumentType = "NATIONAL_ID" | "PASSPORT" | "DRIVERS_LICENSE";

export interface SubmitVerificationRequest {
  documentType: DocumentType;
  documentUrl: string;
  selfieUrl: string;
}

// API Functions

/**
 * Get user's certificates
 */
export async function getUserCertificates(): Promise<Certificate[]> {
  const response = await api.get<Certificate[]>("/certificates");
  return response.data;
}

/**
 * Check certificate eligibility for a course
 */
export async function checkEligibility(
  courseId: string,
): Promise<CertificateEligibility> {
  const response = await api.get<CertificateEligibility>(
    `/certificates/eligibility/${courseId}`,
  );
  return response.data;
}

/**
 * Request certificate for a course
 */
export async function requestCertificate(
  courseId: string,
  recipientName?: string,
): Promise<Certificate> {
  const response = await api.post<Certificate>(`/certificates/${courseId}`, {
    recipientName,
  });
  return response.data;
}

/**
 * Verify a certificate by code (public)
 */
export async function verifyCertificate(
  code: string,
): Promise<CertificateVerification> {
  const response = await api.get<CertificateVerification>(
    `/certificates/verify/${code}`,
  );
  return response.data;
}

/**
 * Get user's identity verification status
 */
export async function getVerificationStatus(): Promise<VerificationStatusResponse> {
  const response = await api.get<VerificationStatusResponse>(
    "/certificates/verification/status",
  );
  return response.data;
}

/**
 * Submit identity verification
 */
export async function submitVerification(
  data: SubmitVerificationRequest,
): Promise<void> {
  await api.post("/certificates/verification", data);
}
